import { useState, useEffect, useCallback } from 'react';

/**
 * Light/Dark theme hook (pilot).
 *
 * - Toggles the `.dark` class on <html>; all semantic tokens (index.css) flip.
 * - Persists to localStorage ('arise-theme'); an inline script in index.html
 *   applies the saved theme before paint (no FOUC).
 * - Default: Day Mode (light) unless a saved choice exists (production-safety default).
 *
 * NOTE (phased rollout): the class is global, but only surfaces migrated to
 * semantic tokens respond to it. Un-converted screens use hardcoded light
 * utilities and stay light until their phase lands.
 */
const STORAGE_KEY = 'arise-theme';

function getInitialTheme() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
  } catch (e) {
    /* ignore */
  }
  // No stored choice: default to Day Mode (light) to minimise change for
  // existing production users. The global toggle opts into Command Mode (dark).
  return 'light';
}

function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
}

export function useDarkMode() {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    applyTheme(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (e) {
      /* ignore */
    }
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  }, []);

  return { theme, isDark: theme === 'dark', toggle, setTheme };
}

export default useDarkMode;
